import type { IEmpresaResumo, StatusEsteira } from '../api';
import type { IEmpresaComStatus } from './derivarProcessos';
import { categoriaDoItem, type PainelDoNo, textoPct } from './painelDoNo';
import { areaDoFluxo } from './analiseBots';

/**
 * EXCEL DO FLUXO (19/10) — pedido do dono:
 *
 * > *"preciso que tenha uma função de eu tirar um Excel de cada fluxo, exemplo
 * > simples nacional, lucro presumido etc, eu conseguir exportar a empresa,
 * > status, erro para eu poder passar pro time"*
 *
 * As linhas saem DAQUI, das mesmas listas e do mesmo `painelDoNo` que desenham
 * os cartões — o backend só formata. Assim a planilha tem exatamente os números
 * da tela no momento do clique (e o recorte da carteira que a tela já aplica).
 *
 * Quatro abas, da mais curta para a mais longa:
 *   1. Resumo  — cada etapa e cada RPA com as parcelas do cartão;
 *   2. Erros   — só erro e alerta, uma linha por empresa × RPA: a lista de trabalho;
 *   3. Empresas — uma linha por empresa do universo, com a situação no fluxo;
 *   4. Detalhe por RPA — empresa × RPA, todas as combinações.
 */

export interface PlanilhaColuna {
  titulo: string;
  largura?: number;
  /** O backend colore a célula pelo texto (ver `CORES_DO_STATUS` lá). */
  status?: boolean;
}

export interface PlanilhaAba {
  nome: string;
  colunas: PlanilhaColuna[];
  linhas: Array<Array<string | number | null>>;
}

export interface PlanilhaDoFluxo {
  fluxo: string;
  cabecalho: string[];
  periodo: string;
  abas: PlanilhaAba[];
}

export interface BotDaEtapa {
  bot: string;
  lista: IEmpresaComStatus[];
  painel: PainelDoNo | null;
}

export interface EtapaDoFluxo {
  nome: string;
  /** Grupo que processa ITENS (ticket, tipo de parcelamento), não empresa. */
  itens: boolean;
  painel: PainelDoNo | null;
  bots: BotDaEtapa[];
}

export interface EntradaDoExcel {
  fluxo: string;
  etapas: EtapaDoFluxo[];
  /** Empresas vigentes do regime do fluxo, já no recorte da tela. */
  universo: string[];
  /** `null` enquanto o `/movimento` não respondeu: ninguém é "sem movimento". */
  comMovimento: Set<string> | null;
  empresas: Map<string, IEmpresaResumo>;
  // eslint-disable-next-line no-unused-vars
  apelidoDoBot: (bot: string) => string;
  /** Linhas de contexto (período, filtros, competência do movimento). */
  contexto: string[];
  periodo: string;
}

/** Os textos que o backend sabe colorir — trocar aqui é trocar lá também. */
export const ROTULO_DO_STATUS: Record<StatusEsteira, string> = {
  done: 'Concluído',
  err: 'Erro',
  warn: 'Alerta',
  run: 'Rodando',
  wait: 'Na fila',
  skip: 'Pendente',
};

export const NAO_RODOU = 'Não rodou';
export const SEM_MOVIMENTO = 'Sem movimento';

export type SituacaoNoFluxo =
  | 'Com erro'
  | 'Em andamento'
  | 'Parcial'
  | 'Não rodou'
  | 'Sem movimento'
  | 'Concluiu o fluxo';

/** Ordem da aba Empresas: o que precisa de ação primeiro. */
const PRIORIDADE: Record<SituacaoNoFluxo, number> = {
  'Com erro': 0,
  'Em andamento': 1,
  Parcial: 2,
  'Não rodou': 3,
  'Sem movimento': 4,
  'Concluiu o fluxo': 5,
};

const ordenarCodigo = (a: string, b: string) => a.localeCompare(b, 'pt-BR', { numeric: true });

const dataHora = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(
    d.getMinutes(),
  )}`;
};

/** A mensagem que a caixa do popup mostraria para o status dele. */
export const mensagemDoItem = (item: IEmpresaComStatus): string | null => {
  if (item.st === 'skip') return item.motivoSkip || item.motivo || null;
  if (item.st === 'err' || item.st === 'warn') return item.motivo || null;
  return null;
};

const movimentoDe = (codi: string, comMovimento: Set<string> | null): string | null => {
  if (!comMovimento) return null;
  return comMovimento.has(codi) ? 'Com movimento' : 'Sem movimento';
};

const responsavelDe = (e: IEmpresaResumo | undefined, fluxo: string): string | null => {
  const areas = e?.areas;
  if (!areas) return null;
  const area = areaDoFluxo(fluxo);
  if (area && areas[area]) return areas[area];
  const todos = Object.values(areas).filter(Boolean);
  return todos.length ? Array.from(new Set(todos)).join(' · ') : null;
};

/**
 * Situação da empresa no fluxo inteiro, pelos status DELA em cada RPA (as etapas
 * de itens ficam de fora: elas não falam de empresa).
 */
export const situacaoNoFluxo = (
  statusPorBot: Array<StatusEsteira | null>,
  semMovimento: boolean,
): SituacaoNoFluxo => {
  const categorias = statusPorBot.map(st =>
    st ? categoriaDoItem({ st } as IEmpresaComStatus) : null,
  );
  if (categorias.includes('erro')) return 'Com erro';
  const ok = categorias.filter(c => c === 'ok').length;
  if (statusPorBot.length && ok === statusPorBot.length) return 'Concluiu o fluxo';
  if (categorias.includes('andamento')) return 'Em andamento';
  if (ok > 0) return 'Parcial';
  return semMovimento ? 'Sem movimento' : 'Não rodou';
};

export const montarPlanilhaDoFluxo = (entrada: EntradaDoExcel): PlanilhaDoFluxo => {
  const { fluxo, etapas, universo, comMovimento, empresas, apelidoDoBot } = entrada;
  const nomeDe = (codi: string) => {
    const e = empresas.get(codi);
    return e?.empresa?.razao_social || e?.empresa?.nome || null;
  };
  const universoOrdenado = [...universo].sort(ordenarCodigo);

  // ── 1. Resumo ──────────────────────────────────────────────────────────────
  const resumo: PlanilhaAba = {
    nome: 'Resumo',
    colunas: [
      { titulo: 'Etapa', largura: 30 },
      { titulo: 'RPA', largura: 34 },
      { titulo: 'Total de empresas', largura: 11 },
      { titulo: 'Concluídas', largura: 11 },
      { titulo: '% concluídas', largura: 11 },
      { titulo: 'Erro ou alerta', largura: 11 },
      { titulo: 'Em andamento', largura: 11 },
      { titulo: 'Não constam', largura: 11 },
      { titulo: 'Sem movimento', largura: 11 },
    ],
    linhas: [],
  };
  const linhaDoPainel = (etapa: string, rpa: string, p: PainelDoNo) => [
    etapa,
    rpa,
    p.base,
    p.ok.length,
    textoPct(p.ok.length, p.base),
    p.erro.length,
    p.andamento.length,
    p.naoConstam.length,
    p.semMovimento.length,
  ];
  etapas.forEach(etapa => {
    if (etapa.itens) {
      // Itens não têm universo: só quantos entraram e quantos concluíram.
      const itens = etapa.bots.flatMap(b => b.lista);
      const ok = itens.filter(i => categoriaDoItem(i) === 'ok').length;
      const erro = itens.filter(i => categoriaDoItem(i) === 'erro').length;
      resumo.linhas.push([
        etapa.nome,
        'Processa itens, não empresas',
        itens.length,
        ok,
        textoPct(ok, itens.length),
        erro,
        null,
        null,
        null,
      ]);
      return;
    }
    if (etapa.painel) resumo.linhas.push(linhaDoPainel(etapa.nome, 'Etapa inteira', etapa.painel));
    etapa.bots.forEach(b => {
      if (b.painel) resumo.linhas.push(linhaDoPainel(etapa.nome, apelidoDoBot(b.bot), b.painel));
    });
  });

  // ── Status de cada empresa em cada RPA ─────────────────────────────────────
  const botsDeEmpresa = etapas
    .filter(e => !e.itens)
    .flatMap(e => e.bots.map(b => ({ etapa: e.nome, ...b })));
  const indice = botsDeEmpresa.map(b => new Map(b.lista.map(i => [i.codiEmp, i])));

  const detalhe: PlanilhaAba = {
    nome: 'Detalhe por RPA',
    colunas: [
      { titulo: 'ID', largura: 9 },
      { titulo: 'Razão social', largura: 40 },
      { titulo: 'Etapa', largura: 26 },
      { titulo: 'RPA', largura: 32 },
      { titulo: 'Status', largura: 14, status: true },
      { titulo: 'Mensagem', largura: 70 },
      { titulo: 'Falhas no período', largura: 10 },
      { titulo: 'Última falha em', largura: 17 },
      { titulo: 'Movimento', largura: 15 },
    ],
    linhas: [],
  };
  const erros: PlanilhaAba = {
    nome: 'Erros',
    colunas: [
      { titulo: 'ID', largura: 9 },
      { titulo: 'Razão social', largura: 40 },
      { titulo: 'CNPJ', largura: 19 },
      { titulo: 'Responsável', largura: 22 },
      { titulo: 'Etapa', largura: 26 },
      { titulo: 'RPA', largura: 32 },
      { titulo: 'Status', largura: 11, status: true },
      { titulo: 'Mensagem', largura: 80 },
      { titulo: 'Falhas no período', largura: 10 },
      { titulo: 'Última falha em', largura: 17 },
    ],
    linhas: [],
  };
  const linhasEmpresas: Array<{ situacao: SituacaoNoFluxo; linha: Array<string | number | null> }> =
    [];

  universoOrdenado.forEach(codi => {
    const info = empresas.get(codi);
    const nome = nomeDe(codi);
    const mov = movimentoDe(codi, comMovimento);
    const semMov = mov === 'Sem movimento';
    const statusPorBot: Array<StatusEsteira | null> = [];
    const faltam: string[] = [];
    const mensagens: string[] = [];
    botsDeEmpresa.forEach((b, i) => {
      const item = indice[i].get(codi);
      const rpa = apelidoDoBot(b.bot);
      statusPorBot.push(item ? item.st : null);
      if (!item) faltam.push(rpa);
      let status: string;
      if (item) status = ROTULO_DO_STATUS[item.st];
      else status = semMov ? SEM_MOVIMENTO : NAO_RODOU;
      const msg = item ? mensagemDoItem(item) : null;
      const falhas = item?.falhas ?? null;
      const ultimaFalha = dataHora(item?.ultimaFalhaEm);
      detalhe.linhas.push([codi, nome, b.etapa, rpa, status, msg, falhas, ultimaFalha, mov]);
      if (item && (item.st === 'err' || item.st === 'warn')) {
        mensagens.push(`${rpa}: ${msg || 'sem mensagem'}`);
        erros.linhas.push([
          codi,
          nome,
          info?.empresa?.cnpj ?? null,
          responsavelDe(info, fluxo),
          b.etapa,
          rpa,
          status,
          msg,
          falhas,
          ultimaFalha,
        ]);
      }
    });
    const situacao = situacaoNoFluxo(statusPorBot, semMov);
    const concluidos = statusPorBot.filter(st => st === 'done').length;
    linhasEmpresas.push({
      situacao,
      linha: [
        codi,
        nome,
        info?.empresa?.cnpj ?? null,
        info?.empresa?.regime ?? null,
        responsavelDe(info, fluxo),
        mov,
        situacao,
        `${concluidos} de ${botsDeEmpresa.length}`,
        statusPorBot.filter(st => st === 'err' || st === 'warn').length,
        faltam.join(', ') || null,
        mensagens.join(' | ') || null,
      ],
    });
  });

  // As etapas de itens entram no detalhe com o próprio item no lugar da empresa.
  etapas
    .filter(e => e.itens)
    .forEach(etapa =>
      etapa.bots.forEach(b =>
        [...b.lista]
          .sort((x, y) => ordenarCodigo(x.codiEmp, y.codiEmp))
          .forEach(item =>
            detalhe.linhas.push([
              item.codiEmp,
              'Item (não é empresa)',
              etapa.nome,
              apelidoDoBot(b.bot),
              ROTULO_DO_STATUS[item.st],
              mensagemDoItem(item),
              item.falhas ?? null,
              dataHora(item.ultimaFalhaEm),
              null,
            ]),
          ),
      ),
    );

  const empresasAba: PlanilhaAba = {
    nome: 'Empresas',
    colunas: [
      { titulo: 'ID', largura: 9 },
      { titulo: 'Razão social', largura: 40 },
      { titulo: 'CNPJ', largura: 19 },
      { titulo: 'Regime', largura: 18 },
      { titulo: 'Responsável', largura: 22 },
      { titulo: 'Movimento', largura: 15 },
      { titulo: 'Situação no fluxo', largura: 17, status: true },
      { titulo: 'RPAs concluídos', largura: 11 },
      { titulo: 'RPAs com erro', largura: 10 },
      { titulo: 'Faltam rodar', largura: 40 },
      { titulo: 'Erros', largura: 80 },
    ],
    linhas: linhasEmpresas
      .sort((a, b) => PRIORIDADE[a.situacao] - PRIORIDADE[b.situacao])
      .map(l => l.linha),
  };

  return {
    fluxo,
    cabecalho: entrada.contexto,
    periodo: entrada.periodo,
    abas: [resumo, erros, empresasAba, detalhe],
  };
};
